import { istek } from './istemci';
import type { Semalar } from './sema';
import type { Kimlik } from './tipler';

/**
 * GÖREV UÇLARI — 17 ucun 13'ü bağlı.
 *
 * BAĞLANMAYAN DÖRT UÇ: `kayit/acik` · `kayit/gunluk` · `kayit/baslat` ·
 * `kayit/bitir`. Dördü de `MESAI_KAYDI_BAS` istiyor ve DEVAM BEYANI SÖZLEŞME
 * GEREĞİ PWA-ONLY. Panelden kayıt başlatılabilseydi "buton beyanı tek kaynak"
 * kuralı delinir, beyanın kim tarafından yapıldığı belirsizleşirdi.
 *
 * YETKİ ALTI SEVİYEDE:
 *   GET  /gunluk · /:id      PERSONEL_GORUNTULE | GOREV_OLUSTUR | GOREV_TIPI_YONET | MESAI_KAYDI_BAS
 *   GET  /tip                PERSONEL_GORUNTULE | GOREV_TIPI_YONET | GOREV_OLUSTUR
 *   GET  /sablon             PERSONEL_GORUNTULE | GOREV_TIPI_YONET     ← GOREV_OLUSTUR YOK
 *   GET  /kalibrasyon · /rapor/*   GOREV_TIPI_YONET | RAPOR_GORUNTULE
 *   POST /gorev · PATCH /:id/iptal            GOREV_OLUSTUR            ← TEK
 *   POST /tip · /sablon · PATCH tip|sablon    GOREV_TIPI_YONET         ← TEK
 *
 * SÜRELER DAKİKA (sayı), ORANLAR ONDALIK METİN. `stdSapmaOrani` ve
 * `sapmaOrani` kayan noktaya çevrilirse eşikler yanlış yerden geçer.
 */

export type Koordinat = Semalar['KoordinatDto'];
export type GorevOzeti = Semalar['GorevOzetiDto'];
export type GorevTipiOzeti = Semalar['GorevTipiOzetiDto'];
export type SablonOzeti = Semalar['SablonOzetiDto'];
export type KalibrasyonSatiri = Semalar['KalibrasyonSatiriDto'];
export type GorevSapmaSatiri = Semalar['GorevSapmaSatiriDto'];
export type GorevsizMesaiSatiri = Semalar['GorevsizMesaiSatiriDto'];
export type GorevAcSonucu = Semalar['GorevAcSonucuDto'];
export type GorevTipiOlusturSonucu = Semalar['GorevTipiOlusturSonucuDto'];
export type SablonOlusturSonucu = Semalar['SablonOlusturSonucuDto'];

export type GorevDurumu = GorevOzeti['durum'];
export type CalismaBicimi = GorevOzeti['calismaBicimi'];
export type Tekrar = SablonOzeti['tekrar'];
export type KalibrasyonDurumu = NonNullable<KalibrasyonSatiri['durum']>;

export const DURUM_ETIKETI: Record<GorevDurumu, string> = {
  planlandi: 'Planlandı',
  basladi: 'Başladı',
  tamamlandi: 'Tamamlandı',
  iptal: 'İptal',
  devredildi: 'Devredildi',
};

export const CALISMA_ETIKETI: Record<CalismaBicimi, string> = {
  birlikte: 'Birlikte (işgücü = süre × kişi)',
  donusumlu: 'Dönüşümlü (nöbet devri)',
};

export const TEKRAR_ETIKETI: Record<Tekrar, string> = {
  gunluk: 'Her gün',
  haftalik: 'Haftanın seçili günleri',
  aylik: 'Ayın belirli günü',
};

export const KALIBRASYON_ETIKETI: Record<KalibrasyonDurumu, string> = {
  veri_yetersiz: 'Veri yetersiz',
  veri_tutarsiz: 'Veri tutarsız',
  on_kalibrasyon: 'Ön kalibrasyon',
  kalibre_edilebilir: 'Kalibre edilebilir',
};

/** ISO gün numarası → kısa ad (1 Pzt … 7 Paz). */
export const GUN_KISALTMASI = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'] as const;

/* --- İstemci tarafı istek şekilleri --------------------------------- */

/**
 * İSTEK ile YANIT ASİMETRİK: istek `enlem`/`boylam` DÜZ alanlar bekliyor,
 * yanıt `koordinat: { enlem, boylam }` nesnesi döndürüyor. Dönüşüm burada
 * yapılır ki ekranlar tek bir şekille çalışsın.
 */
export interface GorevAcGirdisi {
  gorevTipiId: number;
  birimId: Kimlik;
  tarih: string;
  lokasyonMetni?: string;
  koordinat?: Koordinat | null;
  beklenenSureDk?: number;
  beklenenPersonel?: number;
  oncelik?: number;
  aciklama?: string;
}

export interface GorevTipiGirdisi {
  kod: string;
  ad: string;
  birimId: Kimlik;
  beklenenSureDk?: number;
  beklenenPersonel?: number;
  calismaBicimi?: CalismaBicimi;
  aciklama?: string;
}

export interface SablonGirdisi {
  gorevTipiId: number;
  hedefBirimId: Kimlik;
  lokasyonMetni?: string;
  koordinat?: Koordinat | null;
  beklenenSureDk?: number;
  beklenenPersonel?: number;
  tekrar: Tekrar;
  gunSecimi?: number[];
  ayinGunu?: number;
  oncelik?: number;
  aciklama?: string;
}

/** Koordinat nesnesini düz alanlara açar; yoksa hiç göndermez. */
function koordinatiAc(koordinat?: Koordinat | null): { enlem?: number; boylam?: number } {
  if (koordinat === undefined || koordinat === null) return {};
  return { enlem: koordinat.enlem, boylam: koordinat.boylam };
}

/* --- Görevler -------------------------------------------------------- */

/**
 * Günlük görev listesi.
 *
 * `birimId` ZORUNLU (sunucu `@Matches` ile istiyor) ve KAPSAMLA SINIRLI —
 * kapsam dışı birim 403 döner. Ekran birim seçilmeden istek göndermez.
 */
export function gunlukGorevler(birimId: Kimlik, tarih: string): Promise<GorevOzeti[]> {
  return istek<GorevOzeti[]>('/v1/gorev/gunluk', { sorgu: { birimId, tarih } });
}

export function gorevAyrintisi(id: Kimlik): Promise<GorevOzeti> {
  return istek<GorevOzeti>(`/v1/gorev/${id}`);
}

export function gorevAc(girdi: GorevAcGirdisi): Promise<GorevAcSonucu> {
  const { koordinat, ...kalan } = girdi;
  return istek<GorevAcSonucu>('/v1/gorev', {
    yontem: 'POST',
    govde: { ...kalan, ...koordinatiAc(koordinat) },
  });
}

/** Görev SİLİNMEZ, iptal edilir. Gerekçe ZORUNLU. */
export function gorevIptal(id: Kimlik, gerekce: string): Promise<void> {
  return istek<void>(`/v1/gorev/${id}/iptal`, { yontem: 'PATCH', govde: { gerekce } });
}

/* --- Görev tipleri --------------------------------------------------- */

export function gorevTipleri(birimId?: Kimlik): Promise<GorevTipiOzeti[]> {
  return istek<GorevTipiOzeti[]>('/v1/gorev/tip', { sorgu: { birimId } });
}

export function gorevTipiTanimla(girdi: GorevTipiGirdisi): Promise<GorevTipiOlusturSonucu> {
  return istek<GorevTipiOlusturSonucu>('/v1/gorev/tip', { yontem: 'POST', govde: girdi });
}

export function kalibrasyonOnerileri(birimId?: Kimlik): Promise<KalibrasyonSatiri[]> {
  return istek<KalibrasyonSatiri[]>('/v1/gorev/kalibrasyon', { sorgu: { birimId } });
}

/**
 * Beklenen süreyi kalibre eder.
 *
 * SİSTEM ÖNERİR, MÜDÜR KABUL EDER. Otomatik uygulansaydı ölçtüğü şeyin
 * peşinden koşan bir gösterge olurdu: ekip yavaşladıkça beklenen süre de uzar
 * ve sapma hep sıfır görünürdü.
 *
 * `gerekce` İSTEĞE BAĞLI (`KalibreEtDto` `@IsOptional`) — iptal ve pasife
 * almanın aksine.
 */
export function kalibreEt(
  gorevTipiId: number,
  beklenenSureDk: number,
  beklenenPersonel: number,
  gerekce?: string,
): Promise<void> {
  return istek<void>(`/v1/gorev/tip/${gorevTipiId}/kalibre-et`, {
    yontem: 'PATCH',
    govde: { beklenenSureDk, beklenenPersonel, gerekce },
  });
}

/* --- Şablonlar ------------------------------------------------------- */

export function gorevSablonlari(birimId?: Kimlik): Promise<SablonOzeti[]> {
  return istek<SablonOzeti[]>('/v1/gorev/sablon', { sorgu: { birimId } });
}

export function sablonTanimla(girdi: SablonGirdisi): Promise<SablonOlusturSonucu> {
  const { koordinat, ...kalan } = girdi;
  return istek<SablonOlusturSonucu>('/v1/gorev/sablon', {
    yontem: 'POST',
    govde: { ...kalan, ...koordinatiAc(koordinat) },
  });
}

/** Şablon SİLİNMEZ, pasife alınır. Gerekçe ZORUNLU. */
export function sablonPasifeAl(id: Kimlik, gerekce: string): Promise<void> {
  return istek<void>(`/v1/gorev/sablon/${id}/pasife-al`, {
    yontem: 'PATCH',
    govde: { gerekce },
  });
}

/* --- Raporlar -------------------------------------------------------- */

export function gorevSapmaRaporu(
  birimId: Kimlik,
  baslangic: string,
  bitis: string,
): Promise<GorevSapmaSatiri[]> {
  return istek<GorevSapmaSatiri[]>('/v1/gorev/rapor/sapma', {
    sorgu: { birimId, baslangic, bitis },
  });
}

export function gorevsizMesaiRaporu(
  birimId: Kimlik,
  baslangic: string,
  bitis: string,
): Promise<GorevsizMesaiSatiri[]> {
  return istek<GorevsizMesaiSatiri[]>('/v1/gorev/rapor/gorevsiz-mesai', {
    sorgu: { birimId, baslangic, bitis },
  });
}
